import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Text,
  Spinner,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import services from "../Orders/services";
import { showBrowserNotification } from "../../utils/showBrowserNotification";

const processedAdditions = new Set<string>();

const AddCatalogPart: React.FC = () => {
  const { orderNo, partId } = useParams<{ orderNo: string; partId: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isProcessing = useRef(false);

  const fetchData = async () => {
    if (!orderNo || !partId) return;

    try {
      const orderResponse = await services.getAllGaraazOrdersV2(
        `&orderNo=${orderNo}&includeTestOrders=true`,
      );
      if (!orderResponse || orderResponse.length === 0) {
        throw new Error(`Order with order number ${orderNo} not found`);
      }
      const order = orderResponse?.orders?.[0];

      const part = await services.getPartDetailsByPartId(partId);
      if (!part || !part._id) {
        throw new Error(`Invalid part data received for part ID: ${partId}`);
      }

      await services.orderItemsAction(orderNo, "ADD", [
        {
          partId: part._id,
          quantity: 1,
        },
      ]);

      showBrowserNotification(
        "Part Added Successfully",
        `Part Number ${part.partNumber} added successfully in order ${orderNo}`,
      );
    } catch (err: any) {
      showBrowserNotification(
        "Part Addition Failed",
        `Part ${partId} addition failed in order ${orderNo}`,
      );
      setError(err.message || "An error occurred");
    } finally {
      window.close();
    }
  };

  useEffect(() => {
    if (!orderNo || !partId) {
      setError("Missing order number or part ID");
      setLoading(false);
      return;
    }

    const lockKey = `${orderNo}-${partId}`;
    if (isProcessing.current || processedAdditions.has(lockKey)) {
      return;
    }

    // Synchronously lock immediately to catch any simultaneous triggers
    isProcessing.current = true;
    processedAdditions.add(lockKey);

    fetchData();
  }, [orderNo, partId]);

  return (
    <>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          borderRadius={"md"}
        >
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text>Processing part addition in order {orderNo}...</Text>
          </VStack>
        </Box>
      ) : error ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          p={8}
        >
          <Alert status="error" maxWidth="600px">
            <AlertIcon />
            <Box>
              <AlertTitle>Error!</AlertTitle>
              <AlertDescription>
                Error adding part {partId} to order {orderNo}: {error}
              </AlertDescription>
            </Box>
          </Alert>
        </Box>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text>Processing part addition in order {orderNo}...</Text>
          </VStack>
        </Box>
      )}
    </>
  );
};

export default AddCatalogPart;
